/** SLA（初回応答期限）の残り時間・超過幅を数値で表す（改善 D）
 *
 * 「SLA超過」バッジが並ぶだけではどれが最も逼迫しているか判別できないため、
 * 経過／残り時間を数値化し、そのままトリアージ順（既定ソート）にも使う。
 *
 * 対象外の扱い: `sla_due_at` が無いもの、および **初回応答済み（first_response_at あり）**
 * は期限の意味を失うため表示しない。以前は応答後も赤い「SLA超過」バッジが残り、
 * 対応済みのチケットが未対応と同じ見え方をしていた。 */

import type { Ticket } from "../types.ts";

export type SlaLevel = "over" | "critical" | "warn" | "normal" | "none";

export type SlaState = {
  level: SlaLevel;
  /** 表示ラベル（例: `SLA +18h 超過` / `SLA 残り 1h20m`）。level==="none" は空 */
  label: string;
  /** 残り分数。超過時は負値。対象外は null */
  minutes: number | null;
  /** 超過幅が大きい（24時間以上）= バッジを塗りつぶしで最も重く見せる */
  heavy: boolean;
};

const NONE: SlaState = { level: "none", label: "", minutes: null, heavy: false };

const MIN = 60 * 1000;

function fmtOver(mins: number): string {
  const m = Math.abs(mins);
  if (m >= 24 * 60) return `+${Math.floor(m / (24 * 60))}日`;
  if (m >= 60) return `+${Math.floor(m / 60)}h`;
  return `+${m}m`;
}

function fmtLeft(mins: number): string {
  if (mins >= 24 * 60) return `${Math.floor(mins / (24 * 60))}日`;
  if (mins >= 120) return `${Math.floor(mins / 60)}h`;
  if (mins >= 60) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m ? `${h}h${m}m` : `${h}h`;
  }
  return `${mins}m`;
}

export function slaState(
  t: Pick<Ticket, "sla_due_at" | "first_response_at">,
  nowMs: number,
): SlaState {
  if (!t.sla_due_at) return NONE;
  if (t.first_response_at) return NONE; // 初回応答済み = 期限の対象外
  const due = new Date(t.sla_due_at).getTime();
  if (!Number.isFinite(due)) return NONE;

  const minutes = Math.round((due - nowMs) / MIN);
  if (minutes < 0) {
    return {
      level: "over",
      label: `SLA ${fmtOver(minutes)} 超過`,
      minutes,
      heavy: Math.abs(minutes) >= 24 * 60,
    };
  }
  if (minutes < 120) return { level: "critical", label: `SLA 残り ${fmtLeft(minutes)}`, minutes, heavy: false };
  if (minutes < 480) return { level: "warn", label: `SLA 残り ${fmtLeft(minutes)}`, minutes, heavy: false };
  return { level: "normal", label: `SLA 残り ${fmtLeft(minutes)}`, minutes, heavy: false };
}

/** 逼迫順ソート用の値。小さいほど優先（超過は負値・対象外は最後）。 */
export function slaSortValue(
  t: Pick<Ticket, "sla_due_at" | "first_response_at">,
  nowMs: number,
): number {
  const s = slaState(t, nowMs);
  return s.minutes == null ? Number.POSITIVE_INFINITY : s.minutes;
}

/** バッジの配色（level ごと）。CSS クラス名を返す。 */
export function slaBadgeClass(s: SlaState): string {
  return `support-sla-badge support-sla-badge--${s.level}` + (s.heavy ? " support-sla-badge--heavy" : "");
}
